import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { openModal, showPageLoading, showToastMessage } from 'enable-ui';
import { useFormik } from 'formik';

import { useAuthStore, userProfileSelector } from 'modules/auth/core';
import { RoleKey, TrainerRoleKey } from 'modules/auth/core/enum';
import { MODAL_KEY } from 'modules/auth/core/modal-key';
import type { Courses } from 'modules/dashboard/core/type';
import { Station } from 'modules/institution-info/core/type';
import type { Institution } from 'modules/institutions/core/type';
import type { Trainer } from 'modules/trainers/core/type';

import type { Classes } from '../type';

import {
  getExtFileName,
  hasEmptyValues,
  isTypeImage,
  randomString,
  trimObject,
  trimValue,
} from 'helpers';
import { UploadFile } from 'helpers/upload-file';
import { useBoolean, useNumber, useString } from 'hooks';
import { apiCall, useFetchApiToState } from 'hooks/service';
import { useWidthAndHeightFile } from 'hooks/widthAndHeightFile';
import { cloneDeep, debounce, differenceBy, isEmpty, isUndefined } from 'lodash';

import { PATH } from 'constants/path';

export const useClassAddControler = () => {
  const navigateTo = useNavigate();

  const userProfile = useAuthStore(userProfileSelector);

  const institutionId = userProfile?.institution?.id || '';
  const roleKey = userProfile?.role?.key || '';

  const [activePage, setActivePage] = useState<{ isCreate: boolean; isAdd: boolean }>({
    isCreate: true,
    isAdd: false,
  });

  const [selectedTrainers, setSelectedTrainers] = useState<Record<string, string>>({});

  const [trainersData, setTrainersData] = useState<Trainer[]>([]);

  const [coursesData, setCoursesData] = useState<Courses[]>([]);

  const searchTrainerValue = useString('');

  const searchCourseValue = useString('');

  const searchSelect = useString('');

  const [selectedData, setSelectedData] = useState<Trainer[]>([]);

  const [deleteSelected, setDeleteSelected] = useState<Trainer[]>([]);

  const [courseSelected, setCourseSelected] = useState<Courses>();

  const [thumbnail, setThumbnail] = useState<File | null>(null);

  const [pageTrainer, setPageTrainer] = useState<number>(1);

  const totalPageTrainer = useNumber(0);

  const hasMoreTrainer = useBoolean(false);

  const isLoading = useBoolean(false);

  const [stations, setStations] = useState<Station[]>([]);

  const [selectedStation, setSelectedStation] = useState<{ value: ''; label: '' }>();

  const thumbnailUrl = useString('');
  const { handleGetWidthAndHeightThumbnail, thumbnailHeight, thumbnailWidth } =
    useWidthAndHeightFile();

  const institutionState = useFetchApiToState<any, any>({
    url: '/institutions',
    data: {
      page: 1,
      limit: 9999,
    },
    method: 'GET',
  });

  const institutions =
    institutionState.state?.items?.map((elInstitutions: Institution) => ({
      value: elInstitutions.id,
      label: elInstitutions.name,
    })) || [];

  useEffect(() => {
    if (!roleKey) {
      return;
    }

    if (roleKey === RoleKey.BIOLOGIC_ADMIN) {
      institutionState.fetchApi();
    }
  }, [roleKey]);

  useEffect(() => {
    if (!roleKey) {
      return;
    }

    let newInstitutionId = '';

    if (!isEmpty(institutions) && roleKey === RoleKey.BIOLOGIC_ADMIN) {
      newInstitutionId = institutions[0].value;
    }

    if (institutionId) {
      newInstitutionId = institutionId;
    }

    getStations(newInstitutionId);

    setFieldValue('institutionId', newInstitutionId);
  }, [JSON.stringify(institutions), roleKey, institutionId]);

  const getListTrainer = async (search: string = '') => {
    const result: {
      items: Trainer[];
      total: number;
    } = await apiCall({
      url: '/users',
      method: 'GET',
      data: {
        page: pageTrainer.toString(),
        limit: 10,
        institutionId: values.institutionId || institutionId,
        roleKey: RoleKey.TRAINER_INSTITUTION,
        search: search,
      },
    });

    hasMoreTrainer.setValue(result?.items?.length === 10);

    setTimeout(() => {
      isLoading.setValue(false);
    }, 1000);

    const newItems = result?.items ?? [];

    if (pageTrainer < 1) return;

    if (pageTrainer === 1) {
      setTrainersData(newItems);
      totalPageTrainer.setValue(result.total);

      return;
    }

    setTrainersData((prev) => [...prev, ...newItems]);
  };

  const getListCourses = async (search: string = '') => {
    const result: {
      items: Courses[];
      total: number;
    } = await apiCall({
      url: '/courses',
      method: 'GET',
      data: {
        page: 1,
        limit: 10,
        institutionId: values.institutionId,
        search: search,
      },
    });

    if (result?.items) {
      setCoursesData(result?.items ?? []);
    }
  };

  const getStations = async (institution_id: string) => {
    const result: Station[] = await apiCall({
      url: '/stations',
      method: 'GET',
      data: {
        institutionId: institution_id,
      },
    });

    if (isEmpty(result)) {
      return;
    }

    setStations(result);
  };

  const formik = useFormik({
    initialValues: {
      name: '',
      description: '',
      institutionId: '',
    },
    onSubmit: async (values, { setSubmitting }) => {
      if (
        selectedData.some(
          (el) =>
            String(el.trainerRole?.key) === TrainerRoleKey.COACH_MANAGER ||
            String(el.trainerRole?.key) === TrainerRoleKey.TRAIN_THE_TRAINER,
        )
      ) {
        showPageLoading();
        setSubmitting(true);
        let urlThumbnail = '';

        if (thumbnail) {
          const ext = getExtFileName(thumbnail.name);

          urlThumbnail = `thumbnail-class-${randomString(4)}-${Date.now()}${ext}`;

          await UploadFile(thumbnail, urlThumbnail);
        }

        const bodyRequest: any = {
          ...trimObject(values),
          courseId: courseSelected?.id,
          trainerIds: selectedData.map((el) => el.id),
          thumbnail: urlThumbnail,
          institutionId: values.institutionId,
          stationId: selectedStation?.value,
        };

        if (urlThumbnail && thumbnailHeight.value && thumbnailWidth.value) {
          bodyRequest.thumbnailHeight = thumbnailHeight.value;
          bodyRequest.thumbnailWidth = thumbnailWidth.value;
        }

        const result: Classes = await apiCall({
          url: `/classes`,
          method: 'POST',
          data: bodyRequest,
          showError: true,
          isLoading: true,
        });

        if (isEmpty(result?.id)) {
          return;
        }

        navigateTo(PATH.CLASSES);
        showToastMessage('success', 'Added class successfully!');

        return;
      }

      openModal(MODAL_KEY.CREATE_CLASS);
    },
  });

  const { values, isSubmitting, isValid, handleChange, handleSubmit, setFieldValue } = formik;

  useEffect(() => {
    getListCourses();
  }, [values.institutionId]);

  useEffect(() => {
    if (pageTrainer < 1 || !values.institutionId) {
      return;
    }

    getListTrainer();
  }, [values.institutionId, pageTrainer]);

  const disabledNextButton =
    hasEmptyValues({ name: values.name }) ||
    isUndefined(courseSelected?.id) ||
    !values.institutionId;

  const disabledButton = isSubmitting || !isValid || isEmpty(selectedData);

  const goToAddPage = () => {
    setActivePage({ isCreate: false, isAdd: true });
  };

  const goBackToCreatePage = () => {
    setActivePage({ isCreate: true, isAdd: false });
  };

  const onChangeSelectedTrainers = (exercises: string) => () => {
    const newSelected = cloneDeep(selectedTrainers);

    if (!isUndefined(newSelected[exercises])) {
      delete newSelected[exercises];
    } else {
      newSelected[exercises] = exercises;
    }

    setSelectedTrainers(newSelected);
  };

  const onChangeSearchTrainerValue = (event: React.ChangeEvent<HTMLInputElement>) => {
    searchTrainerValue.setValue(event.target.value);
    debounceSearchValue(event.target.value);
  };

  const debounceSearchValue = useCallback(
    debounce((newSearch: string) => {
      getListTrainer(trimValue(newSearch));
    }, 500),
    [values.institutionId],
  );

  const onChangeSearchCourseValue = (event: React.ChangeEvent<HTMLInputElement>) => {
    searchCourseValue.setValue(event.target.value);
    debounceSearchCourseValue(event.target.value);
  };

  const debounceSearchCourseValue = useCallback(
    debounce((newSearch: string) => {
      getListCourses(trimValue(newSearch));
    }, 500),
    [values.institutionId],
  );

  const handleAddTrainersToClass = () => {
    const newSelectedData = trainersData.filter((el) =>
      Object.keys(selectedTrainers).includes(el.id),
    );

    setSelectedData([...selectedData, ...newSelectedData]);
    setSelectedTrainers({});
  };

  const handleDeleteTrainerInClass = (index: number) => {
    const newSelectedData = cloneDeep(selectedData);

    setDeleteSelected([...deleteSelected, newSelectedData[index]]);

    newSelectedData.splice(index, 1);

    setSelectedData(newSelectedData);
  };

  const handleSelectedCourse = (value: Courses) => {
    setCourseSelected(value);
  };

  const onDeleteSelectedCourse = () => {
    setCourseSelected(undefined);
  };

  const onChangeThumbnail = (files: File[]) => {
    if (isEmpty(files)) {
      return;
    }

    const newFile = files[0];

    if (newFile.type && isTypeImage(newFile.type)) {
      setThumbnail(files[0]);
      thumbnailUrl.setValue(URL.createObjectURL(files[0]));
    } else {
      showToastMessage('error', 'Error format video');
    }
  };

  const onDeleteThumbnail = () => {
    setThumbnail(null);
    thumbnailUrl.setValue('');
  };

  const listTrainer = useMemo(() => {
    return differenceBy(trainersData, selectedData, 'id');
  }, [trainersData, selectedData]);

  const goBack = () => {
    navigateTo(PATH.CLASSES);
  };

  const onChangeInstitution = (item: any) => {
    if (!item) {
      return;
    }

    setFieldValue('institutionId', item.value);

    setCourseSelected(undefined);

    setSelectedTrainers({});

    setPageTrainer(1);
  };

  const onChangeSearchInstitution = (item: string) => {
    searchSelect.setValue(item);
  };

  const handleLoadMoreTrainer = () => {
    if (!hasMoreTrainer.value || isLoading.value) {
      return;
    }

    isLoading.setValue(true);

    setTimeout(() => {
      setPageTrainer((prev) => prev + 1);
    }, 1200);
  };

  const onChangeStation = (value: any) => {
    setSelectedStation(value);
  };

  return {
    goBackToCreatePage,
    onChangeSelectedTrainers,
    selectedTrainers,
    trainersData: listTrainer,
    searchTrainerValue,
    onChangeSearchTrainerValue,
    handleAddTrainersToClass,
    handleDeleteTrainerInClass,
    selectedData,
    activePage,
    searchCourseValue,
    onChangeSearchCourseValue,
    goToAddPage,
    coursesData,
    handleSelectedCourse,
    courseSelected,
    onDeleteSelectedCourse,
    disabledNextButton,
    ...values,
    handleChange,
    handleSubmit,
    disabledButton,
    thumbnailUrl,
    onChangeThumbnail,
    onDeleteThumbnail,
    goBack,
    roleKey,
    institutions,
    onChangeInstitution,
    handleGetWidthAndHeightThumbnail,
    onChangeSearchInstitution,
    searchSelect,
    hasMoreTrainer,
    handleLoadMoreTrainer,
    stations,
    onChangeStation,
    selectedStation,
  };
};
